import { apiConfig } from 'src/config/apiConfig';
import axios from 'axios';
import store, { dispatch } from 'src/store/Store';

export interface TokenRefreshResult {
    success: boolean;
    newAccessToken?: string;
    newRefreshToken?: string;
    newAccessTokenExpiry?: string;
    newRefreshTokenExpiry?: string;
}

export interface AuthTokens {
    accessToken: string | null;
    refreshToken: string | null;
    accessTokenExpiry: string | null;
    refreshTokenExpiry: string | null;
}

/**
 * Gets all authentication tokens from localStorage
 * @returns Object containing all tokens and their expiry dates
 */
export const getAuthTokens = (): AuthTokens => {
    return {
        accessToken: window.localStorage.getItem('access'),
        refreshToken: window.localStorage.getItem('refresh'),
        accessTokenExpiry: window.localStorage.getItem('accessTokenExpiry'),
        refreshTokenExpiry: window.localStorage.getItem('refreshTokenExpiry'),
    };
};

/**
 * Stores authentication tokens in localStorage and updates Redux
 * @param tokens - Object containing tokens and their expiry dates
 */
export const setAuthTokens = (tokens: {
    accessToken?: string;
    refreshToken?: string;
    accessTokenExpiry?: string;
    refreshTokenExpiry?: string;
}): void => {
    if (tokens.accessToken) {
        window.localStorage.setItem('access', tokens.accessToken);
        dispatch({ type: 'authReducer/setAuthAccessToken', payload: tokens.accessToken });
    }
    if (tokens.accessTokenExpiry) {
        window.localStorage.setItem('accessTokenExpiry', tokens.accessTokenExpiry);
    }
    if (tokens.refreshToken) {
        window.localStorage.setItem('refresh', tokens.refreshToken);
    }
    if (tokens.refreshTokenExpiry) {
        window.localStorage.setItem('refreshTokenExpiry', tokens.refreshTokenExpiry);
    }
};

/**
 * Attempts to refresh the access token using the provided refresh token
 * @param refreshToken - The refresh token to use for getting a new access token
 * @returns Promise<TokenRefreshResult> - Result of the refresh attempt
 */
export const attemptTokenRefresh = async (refreshToken: string): Promise<TokenRefreshResult> => {
    try {
        const state = store.getState();
        const userId = state.appGeneralReducer.persist.users_me_profile?.id;

        const refreshResponse = await axios.post(
            apiConfig.API_URL + apiConfig.refresh_token,
            { userId: userId, refreshToken: refreshToken },
            { headers: { 'Content-Type': 'application/json' } }
        );

        if (refreshResponse.status === 200 && refreshResponse.data.accessToken) {
            const newAccessToken = refreshResponse.data.accessToken;

            // Store new tokens using centralized helper
            setAuthTokens({
                accessToken: newAccessToken,
                accessTokenExpiry: refreshResponse.data.accessTokenExpiry,
                refreshToken: refreshResponse.data.refreshToken,
                refreshTokenExpiry: refreshResponse.data.refreshTokenExpiry,
            });

            return {
                success: true,
                newAccessToken,
                newRefreshToken: refreshResponse.data.refreshToken,
                newAccessTokenExpiry: refreshResponse.data.accessTokenExpiry,
                newRefreshTokenExpiry: refreshResponse.data.refreshTokenExpiry
            };
        }

        // Clear Redux token
        dispatch({ type: 'authReducer/setAuthAccessToken', payload: '' });
        return { success: false };
    } catch (error) {
        console.error('Token refresh failed:', error);
        return { success: false };
    }
};

/**
 * Clears all authentication tokens from localStorage and Redux store
 */
export const clearAllTokens = (): void => {
    window.localStorage.removeItem('access');
    window.localStorage.removeItem('refresh');
    window.localStorage.removeItem('accessTokenExpiry');
    window.localStorage.removeItem('refreshTokenExpiry');

    // Clear from Redux store
    dispatch({ type: 'authReducer/setAuthAccessToken', payload: null });
};

/**
 * Checks if user has any tokens stored
 */
export const hasAnyTokens = (): boolean => {
    return !!(window.localStorage.getItem('access') || window.localStorage.getItem('refresh'));
};
